import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ONE } from '@/constants';
import { useDeleteMilestone, useUpdateMilestone } from '@/hooks';
import {
  Icon,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@/ui';

import type { Milestone } from '@/types';
import type { FC, MouseEvent } from 'react';

import MilestoneDialog from '@/components/Growth/MilestoneDialog';
import { api } from '@/store/api';
import { useAppDispatch } from '@/store/hooks';

interface DropdownProps {
  milestone: Milestone;
}

const Dropdown: FC<DropdownProps> = ({ milestone }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const onCloseMenu = () => setAnchor(null);
  const onClick = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchor(event.currentTarget);

  const updateMilestone = useUpdateMilestone();
  const deleteMilestone = useDeleteMilestone();

  const onEdit = () => {
    setOpen(true);
    onCloseMenu();
  };

  const onToggleComplete = () => {
    updateMilestone.mutate(
      { id: milestone.id, isComplete: !milestone.completedAt },
      {
        onSuccess: () => {
          dispatch(api.util.invalidateTags(['GrowthPlan', 'Milestone']));
          onCloseMenu();
        },
      },
    );
  };

  const onDelete = () => {
    deleteMilestone.mutate(milestone.id, {
      onSuccess: () => {
        navigate(-ONE);
        dispatch(api.util.invalidateTags(['GrowthPlan']));
      },
    });
  };

  const markCompleteText = `Mark ${
    milestone.completedAt ? 'not ' : ''
  }complete`;

  return (
    <>
      <IconButton onClick={onClick}>
        <Icon>more_vert</Icon>
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={onCloseMenu}>
        {!!milestone.completedAt && (
          <MenuItem onClick={onEdit}>
            <ListItemIcon>
              <Icon>edit</Icon>
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {!!milestone.steps.length && (
          <MenuItem onClick={onToggleComplete}>
            <ListItemIcon>
              <Icon>check</Icon>
            </ListItemIcon>
            <ListItemText>{markCompleteText}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={onDelete}>
          <ListItemIcon>
            <Icon>trash</Icon>
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      <MilestoneDialog isOpen={open} milestone={milestone} setOpen={setOpen} />
    </>
  );
};

export default Dropdown;
